import React from 'react';

/**
 * Local icon set, replacing react-icons imports.
 *
 * react-icons/fa and react-icons/md each ship one large file containing
 * every icon in the set as a named export; Turbopack's production build
 * was not eliminating the ~1000 unused icons per set (~780 KiB of dead
 * code shipped for the 13 icons actually used site-wide). These components
 * replicate react-icons' IconBase rendering exactly (stroke/fill/strokeWidth,
 * 1em default size, className/style/size passthrough) using the same SVG
 * path data, so every existing usage - including `styled(MdCheckCircle)` -
 * keeps working unchanged.
 */

export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  size?: string | number;
}

function createIcon(viewBox: string, paths: React.SVGAttributes<SVGPathElement>[], displayName: string) {
  const Icon = React.forwardRef<SVGSVGElement, IconProps>(({ size, ...rest }, ref) => (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox={viewBox}
      height={size || '1em'}
      width={size || '1em'}
      xmlns="http://www.w3.org/2000/svg"
      ref={ref}
      {...rest}
    >
      {paths.map((p, i) => (
        <path key={i} {...p} />
      ))}
    </svg>
  ));
  Icon.displayName = displayName;
  return Icon;
}

export const FaStar = createIcon(
  '0 0 576 512',
  [{ d: 'M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z' }],
  'FaStar'
);

export const FaChevronDown = createIcon(
  '0 0 448 512',
  [{ d: 'M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z' }],
  'FaChevronDown'
);

export const FaChevronUp = createIcon(
  '0 0 448 512',
  [{ d: 'M240.971 130.524l194.343 194.343c9.373 9.373 9.373 24.569 0 33.941l-22.667 22.667c-9.357 9.357-24.522 9.375-33.901.04L224 227.495 69.255 381.516c-9.379 9.335-24.544 9.317-33.901-.04l-22.667-22.667c-9.373-9.373-9.373-24.569 0-33.941L207.03 130.525c9.372-9.373 24.568-9.373 33.941-.001z' }],
  'FaChevronUp'
);

export const FaChevronLeft = createIcon(
  '0 0 320 512',
  [{ d: 'M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z' }],
  'FaChevronLeft'
);

export const FaChevronRight = createIcon(
  '0 0 320 512',
  [{ d: 'M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z' }],
  'FaChevronRight'
);

export const FaTimes = createIcon(
  '0 0 352 512',
  [{ d: 'M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z' }],
  'FaTimes'
);

export const FaPaperPlane = createIcon(
  '0 0 512 512',
  [{ d: 'M476 3.2L12.5 270.6c-18.1 10.4-15.8 35.6 2.2 43.2L121 358.4l287.3-253.2c5.5-4.9 13.3 2.6 8.6 8.3L176 407v80.5c0 23.6 28.5 32.9 42.5 15.8L282 426l124.6 52.2c14.2 6 30.4-2.9 33-18.2l72-432C515 7.8 493.3-6.8 476 3.2z' }],
  'FaPaperPlane'
);

export const FaExternalLinkAlt = createIcon(
  '0 0 512 512',
  [{ d: 'M432,320H400a16,16,0,0,0-16,16V448H64V128H208a16,16,0,0,0,16-16V80a16,16,0,0,0-16-16H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V336A16,16,0,0,0,432,320ZM488,0h-128c-21.37,0-32.05,25.91-17,41l35.73,35.73L135,320.37a24,24,0,0,0,0,34L157.67,377a24,24,0,0,0,34,0L435.28,133.32,471,169c15,15,41,4.5,41-17V24A24,24,0,0,0,488,0Z' }],
  'FaExternalLinkAlt'
);

export const FaComments = createIcon(
  '0 0 576 512',
  [{ d: 'M416 192c0-88.4-93.1-160-208-160S0 103.6 0 192c0 34.3 14.1 65.9 38 92-13.4 30.2-35.5 54.2-35.8 54.5-2.2 2.3-2.8 5.7-1.5 8.7S4.8 352 8 352c36.6 0 66.9-12.3 88.7-25 32.2 15.7 70.3 25 111.3 25 114.9 0 208-71.6 208-160zm122 220c23.9-26 38-57.7 38-92 0-66.9-53.5-124.2-129.3-148.1.9 6.6 1.3 13.3 1.3 20.1 0 105.9-107.7 192-240 192-10.8 0-21.3-.8-31.7-1.9C207.8 439.6 281.8 480 368 480c41 0 79.1-9.2 111.3-25 21.8 12.7 52.1 25 88.7 25 3.2 0 6.1-1.9 7.3-4.8 1.3-2.9.7-6.3-1.5-8.7-.3-.3-22.4-24.2-35.8-54.5z' }],
  'FaComments'
);

export const FaBars = createIcon(
  '0 0 448 512',
  [{ d: 'M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z' }],
  'FaBars'
);

export const MdCheckCircle = createIcon(
  '0 0 24 24',
  [
    { fill: 'none', d: 'M0 0h24v24H0z' },
    { d: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z' },
  ],
  'MdCheckCircle'
);

export const MdWarning = createIcon(
  '0 0 24 24',
  [
    { fill: 'none', d: 'M0 0h24v24H0z' },
    { d: 'M1 21h22L12 2zm12-3h-2v-2h2zm0-4h-2v-4h2z' },
  ],
  'MdWarning'
);

export const MdClose = createIcon(
  '0 0 24 24',
  [
    { fill: 'none', d: 'M0 0h24v24H0z' },
    { d: 'M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' },
  ],
  'MdClose'
);

export const MdOutlineKeyboardArrowDown = createIcon(
  '0 0 24 24',
  [{ d: 'M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z' }],
  'MdOutlineKeyboardArrowDown'
);

export const AiOutlineCloseCircle = React.forwardRef<SVGSVGElement, IconProps>(({ size, ...rest }, ref) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    fillRule="evenodd"
    strokeWidth="0"
    viewBox="0 0 1024 1024"
    height={size || '1em'}
    width={size || '1em'}
    xmlns="http://www.w3.org/2000/svg"
    ref={ref}
    {...rest}
  >
    <path d="M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64Zm0 76c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-166.6 372-372-166.6-372-372-372Zm128.013 198.826c.023.007.042.018.083.059l45.02 45.019c.04.04.05.06.058.083a.118.118 0 0 1 0 .07c-.007.022-.018.041-.059.082L557.254 512l127.861 127.862a.268.268 0 0 1 .05.06l.009.023a.118.118 0 0 1 0 .07c-.007.022-.018.041-.059.082l-45.019 45.02c-.04.04-.06.05-.083.058a.118.118 0 0 1-.07 0c-.022-.007-.041-.018-.082-.059L512 557.254 384.14 685.115c-.042.041-.06.052-.084.059a.118.118 0 0 1-.07 0c-.022-.007-.041-.018-.082-.059l-45.02-45.019c-.04-.04-.05-.06-.058-.083a.118.118 0 0 1 0-.07c.007-.022.018-.041.059-.082L466.745 512l-127.86-127.86a.268.268 0 0 1-.05-.061l-.009-.023a.118.118 0 0 1 0-.07c.007-.022.018-.041.059-.082l45.019-45.02c.04-.04.06-.05.083-.058a.118.118 0 0 1 .07 0c.022.007.041.018.082.059L512 466.745l127.862-127.86c.04-.041.06-.052.083-.059a.118.118 0 0 1 .07 0Z" />
  </svg>
));
AiOutlineCloseCircle.displayName = 'AiOutlineCloseCircle';
